import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

// Services
import { userService } from '../../services/users';
import { albumsService } from '../../services/albums';
import { artistService } from '../../services/artist';
import { recordingsService, type TrackMatchSummary } from '../../services/recordings';
import spotifyCacheService from '../../services/spotifyCache';
import {
  extractSpotifyTrackId as extractSpotifyTrackIdFromTrack,
  hydrateTracksWithMatches,
} from '../../utils/trackMatches';

// Interfaces
import type { Album, AlbumFullObject } from '../../interfaces/albums';
import type { Artist } from '../../interfaces/artist';
import type { Track, TrackWithSave } from '../../interfaces/track';
import { RootState } from '../store';

const TRACK_PAGE_SIZE = 50;
const SAVED_TRACKS_CHUNK_SIZE = 50;

const chunkArray = <T>(source: T[], size: number): T[][] => {
  if (size <= 0) return [source.slice()];
  const chunks: T[][] = [];
  for (let index = 0; index < source.length; index += size) {
    chunks.push(source.slice(index, index + size));
  }
  return chunks;
};

const initialState: {
  artist: Artist | null;
  tracks: TrackWithSave[];
  album: AlbumFullObject | null;

  otherAlbums: Album[];

  loading: boolean;
  following: boolean;

  order: string;
  view: 'LIST' | 'COMPACT';
} = {
  tracks: [],
  album: null,
  artist: null,
  otherAlbums: [],

  loading: true,
  following: false,

  order: 'ALL',
  view: 'LIST',
};

const buildFallbackArtist = (album: AlbumFullObject): Artist => {
  const [primaryArtist] = Array.isArray(album.artists) ? album.artists : [];
  const fallbackId =
    primaryArtist?.id && typeof primaryArtist.id === 'string' && primaryArtist.id.trim()
      ? primaryArtist.id
      : `unknown-${album.id}`;

  return {
    external_urls: {
      spotify:
        (primaryArtist?.external_urls && typeof primaryArtist.external_urls.spotify === 'string'
          ? primaryArtist.external_urls.spotify
          : '') || ''
    },
    followers: {
      href: '',
      total: 0,
    },
    genres: [],
    href: (primaryArtist?.href ?? '') || '',
    id: fallbackId,
    images: Array.isArray(album.images) ? album.images : [],
    name: (primaryArtist?.name ?? '') || 'Unknown Artist',
    popularity: 0,
    type: 'artist',
    uri: (primaryArtist?.uri ?? '') || '',
  };
};

const stripTrackSaveFlag = (tracks: TrackWithSave[]): Track[] =>
  tracks.map(({ saved, ...rest }) => ({ ...rest } as Track));

const fetchSavedTrackStates = async (trackIds: string[]): Promise<boolean[]> => {
  if (!trackIds.length) return [];
  const chunks = chunkArray(trackIds, SAVED_TRACKS_CHUNK_SIZE);

  const responses = await Promise.all(
    chunks.map(async (chunk) => {
      if (!chunk.length) return [];
      try {
        const { data } = await userService.checkSavedTracks(chunk);
        return Array.isArray(data) && data.length === chunk.length
          ? data
          : new Array(chunk.length).fill(false);
      } catch {
        return new Array(chunk.length).fill(false);
      }
    })
  );

  return responses.flat();
};

const fetchAllAlbumTracks = async (albumId: string): Promise<Track[]> => {
  const tracks: Track[] = [];
  let offset = 0;

  while (true) {
    const { data } = await albumsService.fetchAlbumTracks(albumId, { limit: TRACK_PAGE_SIZE, offset });
    const items = Array.isArray(data.items) ? data.items : [];
    if (!items.length) break;
    tracks.push(...items);
    offset += items.length;
    if (!data.next) {
      break;
    }
    if (typeof data.total === 'number' && offset >= data.total) {
      break;
    }
  }

  return tracks;
};

const fetchAlbumFromCache = async (
  spotifyAlbumId: string,
  options: { isAuthenticated: boolean }
): Promise<[AlbumFullObject, TrackWithSave[], boolean, Artist, Album[]] | null> => {
  const fallback = await spotifyCacheService.fetchAlbumWithTracks(spotifyAlbumId);
  const album = fallback.album;
  if (!album) return null;

  const baseTracks: Track[] = Array.isArray(fallback.tracks) && fallback.tracks.length
    ? fallback.tracks
    : Array.isArray(album.tracks)
    ? (album.tracks as Track[])
    : [];

  const itemsWithSave: TrackWithSave[] = baseTracks.map((track) => ({
    ...track,
    saved: false,
  }));

  const trackIdsForMatches = Array.from(
    new Set(
      itemsWithSave
        .map((track) => extractSpotifyTrackIdFromTrack(track))
        .filter((trackId): trackId is string => Boolean(trackId))
    )
  );

  let matches: Record<string, TrackMatchSummary> = {};
  if (trackIdsForMatches.length) {
    try {
      matches = await recordingsService.fetchTrackMatches(trackIdsForMatches);
    } catch (matchError) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[albumSlice] Failed to fetch track matches during fallback', matchError);
      }
    }
  }

  const hydratedTracks = hydrateTracksWithMatches(itemsWithSave, matches);
  const sanitizedAlbumTracks = stripTrackSaveFlag(hydratedTracks);

  const primaryArtistId =
    Array.isArray(album.artists) && album.artists.length && album.artists[0].id
      ? album.artists[0].id
      : null;

  let artist: Artist | null = null;
  if (primaryArtistId) {
    try {
      artist = await spotifyCacheService.fetchArtist(primaryArtistId);
    } catch (artistError) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[albumSlice] Failed to load artist from cache during fallback', primaryArtistId, artistError);
      }
    }
  }

  if (!artist) {
    artist = buildFallbackArtist(album);
  }

  let otherAlbums: Album[] = [];
  if (primaryArtistId) {
    try {
      otherAlbums = await spotifyCacheService.fetchAlbumsForArtist(primaryArtistId);
    } catch (artistAlbumsError) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[albumSlice] Failed to load artist albums from cache during fallback', primaryArtistId, artistAlbumsError);
      }
    }
  }

  const albumWithTracks: AlbumFullObject = {
    ...album,
    tracks: sanitizedAlbumTracks,
  };

  return [albumWithTracks, hydratedTracks, false, artist, otherAlbums];
};

const scheduleSavedStateHydration = (
  dispatch: any,
  albumId: string,
  tracks: TrackWithSave[],
  options: { isAuthenticated: boolean }
) => {
  if (!options.isAuthenticated) return;

  const trackIdIndexPairs = tracks
    .map((track) => track.id)
    .filter((id): id is string => Boolean(id && id.trim()));

  void (async () => {
    try {
      const response = await albumsService.checkSavedAlbums([albumId]);
      const data = response.data ?? [];
      const following = Boolean(Array.isArray(data) ? data[0] : false);
      dispatch(albumActions.setFollowing({ following }));
    } catch {
      // best-effort; ignore errors
    }

    if (trackIdIndexPairs.length) {
      try {
        const savedFlags = await fetchSavedTrackStates(trackIdIndexPairs);
        trackIdIndexPairs.forEach((trackId, idx) => {
          const flag = savedFlags[idx];
          if (typeof flag === 'boolean') {
            dispatch(albumActions.updateTrackLikeState({ id: trackId, saved: flag }));
          }
        });
      } catch {
        // best-effort; ignore errors
      }
    }
  })();
};

const scheduleOtherAlbumsHydration = async (
  dispatch: any,
  artistId: string | null,
  currentAlbumId: string | null
) => {
  if (!artistId) return;
  try {
    const albums = await artistService
      .fetchArtistAlbums(artistId, { limit: 10 })
      .then((resp) => ((resp.data as any)?.items as Album[]) ?? []);
    const filtered =
      currentAlbumId && albums.length ? albums.filter((entry) => entry?.id !== currentAlbumId) : albums;
    dispatch(albumActions.setOtherAlbums({ otherAlbums: filtered }));
  } catch {
    // best-effort; ignore errors
  }
};

export const fetchAlbum = createAsyncThunk<
  [AlbumFullObject, TrackWithSave[], boolean, Artist, Album[]],
  string
>('album/fetchAlbum', async (id, params) => {
  const state = params.getState() as RootState;
  const user = state.auth.user;

  const tryCache = async () => {
    try {
      return await fetchAlbumFromCache(id, { isAuthenticated: Boolean(user) });
    } catch (cacheError) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[albumSlice] Cache fetch failed, will try Spotify API', { id, cacheError });
      }
      return null;
    }
  };

  const cachedResult = await tryCache();
  if (cachedResult) {
    scheduleSavedStateHydration(params.dispatch, id, cachedResult[1], { isAuthenticated: Boolean(user) });
    void scheduleOtherAlbumsHydration(
      params.dispatch,
      cachedResult[3]?.id ?? null,
      cachedResult[0]?.id ?? null
    );
    return cachedResult;
  }

  try {
    const albumResponse = await albumsService.fetchAlbum(id);
    const album = albumResponse.data as AlbumFullObject;

    const [tracks] = await Promise.all([fetchAllAlbumTracks(id)]);

    const itemsWithSave: TrackWithSave[] = tracks.map((track) => ({
      ...track,
      saved: false,
    }));

    const trackIds = Array.from(
      new Set(
        itemsWithSave
          .map((track) => extractSpotifyTrackIdFromTrack(track))
          .filter((id): id is string => Boolean(id))
      )
    );

    const matches: Record<string, TrackMatchSummary> = trackIds.length
      ? await recordingsService.fetchTrackMatches(trackIds)
      : {};

    const hydratedTracks = hydrateTracksWithMatches(itemsWithSave, matches);

    const primaryArtistId =
      Array.isArray(album.artists) && album.artists.length && album.artists[0].id
        ? album.artists[0].id
        : null;

    const artist = primaryArtistId
      ? await artistService.fetchArtist(primaryArtistId).then((resp) => resp.data as Artist)
      : buildFallbackArtist(album);

    scheduleSavedStateHydration(params.dispatch, id, hydratedTracks, { isAuthenticated: Boolean(user) });
    void scheduleOtherAlbumsHydration(params.dispatch, primaryArtistId, album.id);

    return [album, hydratedTracks, false, artist ?? buildFallbackArtist(album), []];
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[albumSlice] Falling back to backend cache for album data', { id, error });
    }

    const fallback = await tryCache();
    if (!fallback) {
      throw error;
    }
    scheduleSavedStateHydration(params.dispatch, id, fallback[1], { isAuthenticated: Boolean(user) });
    void scheduleOtherAlbumsHydration(
      params.dispatch,
      fallback[3]?.id ?? null,
      fallback[0]?.id ?? null
    );
    return fallback;
  }
});

const albumSlice = createSlice({
  name: 'album',
  initialState,
  reducers: {
    setFollowing(state, action: PayloadAction<{ following: boolean }>) {
      state.following = action.payload.following;
    },
    setAlbum(state, action: PayloadAction<{ album: AlbumFullObject | null }>) {
      state.album = action.payload.album;
      if (!action.payload.album) {
        state.tracks = [];
        state.following = false;
        state.loading = true;
        state.artist = null;
        state.view = 'LIST';
      }
    },
    setView(state, action: PayloadAction<{ view: 'LIST' | 'COMPACT' }>) {
      state.view = action.payload.view;
    },
    setOrder(state, action: PayloadAction<{ order: string }>) {
      state.order = action.payload.order;
    },
    updateTrackLikeState(state, action: PayloadAction<{ id: string; saved: boolean }>) {
      const index = state.tracks.findIndex((track) => track.id === action.payload.id);
      if (index !== -1) state.tracks[index].saved = action.payload.saved;
    },
    setTracksSavedState(state, action: PayloadAction<{ trackIds: string[]; saved: boolean }>) {
      const ids = action.payload.trackIds.filter(Boolean);
      if (!ids.length) return;

      const idSet = new Set(ids);
      state.tracks.forEach((track) => {
        if (idSet.has(track.id)) {
          track.saved = action.payload.saved;
        }
      });
    },
    resetOrder(state, action: PayloadAction<{ order?: string }>) {
      state.order = action.payload.order || 'ALL';
    },
    setOtherAlbums(state, action: PayloadAction<{ otherAlbums: Album[] }>) {
      state.otherAlbums = action.payload.otherAlbums;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAlbum.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAlbum.fulfilled, (state, action) => {
      state.album = action.payload[0];
      state.tracks = action.payload[1];
      state.artist = action.payload[3];
      state.following = action.payload[2];
      state.otherAlbums = action.payload[4];
      state.loading = false;
    });
  },
});

export const albumActions = {
  fetchAlbum,
  ...albumSlice.actions,
};

export default albumSlice.reducer;
